import React from "react";
import { Link } from "react-router-dom";
import { appHistory } from "@/router/history";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";
import dayjs from "@/config/dayjs";

import MaintenanceApi from "api/MaintenanceApi";
import { withMaintenanceData, type MaintenanceData } from "api/queries/useMaintenance";
import { Button } from "./ui";

interface MaintenancePageOwnProps extends WithTranslation {}

interface MaintenancePageStateProps {
  maintenance: MaintenanceData;
}

type MaintenancePageProps = MaintenancePageOwnProps & MaintenancePageStateProps;

function MaintenancePage({ t, i18n, maintenance }: MaintenancePageProps) {
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const isRequestPendingRef = React.useRef(false);

  const checkMaintenanceStatus = React.useCallback((showLoader = true): Promise<unknown> => {
    if (isRequestPendingRef.current) {
      return Promise.resolve(null);
    }

    isRequestPendingRef.current = true;

    if (showLoader) {
      setIsRefreshing(true);
    }

    return MaintenanceApi.getMaintenance()
      .then((data: unknown) => {
        const payload = data as MaintenanceData | null;
        if (!payload?.isMaintenance) {
          appHistory.push(BASE_PATH + "/");
        }
        return data;
      })
      .catch(() => null)
      .finally(() => {
        isRequestPendingRef.current = false;
        if (showLoader) {
          setIsRefreshing(false);
        }
      });
  }, []);

  React.useEffect(() => {
    const pollInterval = window.setInterval(() => {
      checkMaintenanceStatus(false);
    }, 5000);
    return () => {
      window.clearInterval(pollInterval);
    };
  }, [checkMaintenanceStatus]);

  const formatDate = (value?: string | null) => {
    if (!value) return null;
    const locale = i18n?.resolvedLanguage || i18n?.language || "fr";
    return dayjs(value).locale(locale).format("L LT");
  };

  const getPeriodLabel = () => {
    const startsOn = formatDate(maintenance?.starts_on);
    const endsOn = formatDate(maintenance?.ends_on);

    if (!endsOn) return t("maintenance.period_indefinite");
    if (!startsOn) return t("maintenance.period_until", { date: endsOn });
    return t("maintenance.period_range", { start: startsOn, end: endsOn });
  };

  const handleRefresh = () => {
    checkMaintenanceStatus(true);
  };

  return (
    <div className="Page AuthPage MaintenancePageShell">
      <div className="Page-content AuthPage-content">
        <div className="AuthPage-header">
          <div className="LogoCompanyWrapper">
            <Link to={BASE_PATH + "/"}>
              <div className="LogoCompany isSpaced isGMC"></div>
            </Link>
          </div>
          <p className="AuthPage-kicker">{t("topbar.platform_label")}</p>
          <h2 className="Page-title isSpaced">{t("maintenance.title")}</h2>
          <p className="AuthPage-intro">{getPeriodLabel()}</p>
        </div>

        <div className="AuthPage-panel AuthPage-panel--compact MaintenancePage-panel">
          <p className="MaintenancePage-message">
            {maintenance?.message || t("maintenance.default_message")}
          </p>
          <div className="MaintenancePage-actions">
            <Button
              size="lg"
              className="Button isLarge MaintenancePage-refreshButton"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? t("maintenance.refreshing") : t("maintenance.refresh")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default compose(withTranslation(), withMaintenanceData)(MaintenancePage);
